import { Fragment } from "react";

import { cn } from "@/lib/utils";
import PrintSheet, { PrintUnit, SheetTitle, TD, TH } from "@/modules/print/sheet";
import { snapshotVatMode, SnapshotData, SnapshotItem } from "@buildpy/core/modules/quote/types";
import { unitPriceHeading } from "@buildpy/core/modules/quote/snapshot-vat";
import { formatQty, formatAmount } from "@/modules/quote/ui/format";
import Money from "@/components/money";

interface Props {
  data: SnapshotData;
}

// 인쇄 내용폭 182mm ≈ 688px — 금액 열 11%(안쪽 ≈71px, 좌우 2px) · 범위 7 · 작업내역 15 · 규격·수량 7 · 비고 나머지 9%.
export const QUOTE_DETAIL_COLS = ["w-[7%]", "w-[15%]", "w-[7%]", "w-[7%]", "w-[11%]", "w-[11%]", "w-[11%]", "w-[11%]", "w-[11%]"] as const;

// 표시 문자열(부호 포함) 11자부터(9자리·음수 8자리) 11.2px 로는 칸을 넘친다 — 10px·tracking-tighter 로 줄인다.
export const AMOUNT_SHRINK_LEN = 11;

// 표시 문자열(부호·콤마 포함) 14자부터(11자리·음수 10자리)는 10px 로도 칸(≈70px)을 넘어 잘린다 — 콤마 뒤에서 줄을 접는다.
export const AMOUNT_WRAP_LEN = 14;

const fit = (text: string) => {
  if (text.length < AMOUNT_SHRINK_LEN) return text;
  if (text.length < AMOUNT_WRAP_LEN) return <span className="text-3xs tracking-tighter" data-amount-fit="shrink">{text}</span>;
  return (
    <span className="text-3xs tracking-tighter whitespace-normal" data-amount-fit="wrap">
      {text.split(",").map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <>,<wbr /></>}
          {part}
        </Fragment>
      ))}
    </span>
  );
};

const won = (value: number) => (value > 0 ? fit(formatAmount(value)) : "");
const total = (value: number) => fit(formatAmount(value));

const MONEY = "px-0.5 text-right tabular-nums whitespace-nowrap";

/** SET 부모는 자기 단가가 없다. 빈 칸으로 두고 금액만 자식 합으로 보인다. */
const ItemRows = ({
  items,
  depth = 0,
}: {
  items: SnapshotItem[];
  depth?: number;
}) =>
  items.map((item, index) => {
    const isParent = item.children.length > 0;
    return (
      <Fragment key={`${depth}-${index}-${item.item}`}>
        <tr>
          <TD className="text-[#525252]">{item.scope}</TD>
          <TD
            className={cn(isParent && "font-medium")}
            style={depth > 0 ? { paddingLeft: `${8 + depth * 12}px` } : undefined}>
            {depth > 0 && <span className="text-[#737373]">└ </span>}
            {item.item}
          </TD>
          <TD><PrintUnit unit={item.unit} /></TD>
          <TD className="text-right tabular-nums">
            {isParent ? "" : formatQty(item.qty)}
          </TD>
          <TD className={MONEY}>
            {isParent ? "" : won(item.material_unit_price)}
          </TD>
          <TD className={MONEY}>{won(item.material_amount)}</TD>
          <TD className={MONEY}>
            {isParent ? "" : won(item.labor_unit_price)}
          </TD>
          <TD className={MONEY}>{won(item.labor_amount)}</TD>
          <TD className={cn(MONEY, "font-medium")}>
            {won(item.amount)}
          </TD>
          <TD className="text-[#525252]">{item.note}</TD>
        </tr>
        {isParent && <ItemRows depth={depth + 1} items={item.children} />}
      </Fragment>
    );
  });

/** 상세 견적서. 공종 하나가 표 하나이고, 페이지는 공종 중간에서도 넘어간다. */
const QuoteDetailSheet = ({ data }: Props) => {
  const trades = [...data.trades].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <PrintSheet>
      <SheetTitle>상세 견적서</SheetTitle>
      <p className="-mt-2 mb-2 text-right text-xs text-[#525252]" data-testid="quote-detail-vat-basis">
        {unitPriceHeading(snapshotVatMode(data))} · 단위 원
      </p>

      <div className="flex flex-col gap-y-4">
        {trades.length === 0 && (
          <p className="py-6 text-center text-xs text-[#737373]">
            공종이 없습니다.
          </p>
        )}

        {trades.map((trade) => (
          <div key={`${trade.trade}-${trade.sort_order}`}>
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold">{trade.name}</h3>
              <Money className="text-xs" value={trade.amount} />
            </div>

            <table className="w-full table-fixed border-collapse">
              <colgroup>
                {QUOTE_DETAIL_COLS.map((width, i) => (
                  <col key={i} className={width} />
                ))}
                <col />
              </colgroup>
              <thead>
                <tr>
                  <TH>범위</TH>
                  <TH>작업내역</TH>
                  <TH>규격</TH>
                  <TH>수량</TH>
                  <TH>자재 단가</TH>
                  <TH>자재 금액</TH>
                  <TH>노무 단가</TH>
                  <TH>노무 금액</TH>
                  <TH>합계</TH>
                  <TH>비고</TH>
                </tr>
              </thead>
              <tbody>
                {trade.items.length === 0 ? (
                  <tr>
                    <TD className="py-3 text-center text-[#737373]" colSpan={10}>
                      항목이 없습니다.
                    </TD>
                  </tr>
                ) : (
                  <ItemRows items={trade.items} />
                )}
                <tr>
                  <TD className="bg-[#fafafa] font-semibold" colSpan={5}>
                    {trade.name} 소계
                  </TD>
                  <TD className={cn(MONEY, "bg-[#fafafa] font-semibold")}>
                    {total(trade.material_amount)}
                  </TD>
                  <TD className="bg-[#fafafa]" />
                  <TD className={cn(MONEY, "bg-[#fafafa] font-semibold")}>
                    {total(trade.labor_amount)}
                  </TD>
                  <TD className={cn(MONEY, "bg-[#fafafa] font-semibold")}>
                    {total(trade.amount)}
                  </TD>
                  <TD className="bg-[#fafafa]" />
                </tr>
              </tbody>
            </table>

            {trade.condition_text && (
              <p className="mt-1 text-xs text-[#525252]">
                * {trade.condition_text}
              </p>
            )}
          </div>
        ))}
      </div>
    </PrintSheet>
  );
};

export default QuoteDetailSheet;
